import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LegalOpinionRequest, LegalOpinionStatus, LegalOpinionUrgency, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const URGENCIES: LegalOpinionUrgency[] = ['ROUTINE', 'URGENT', 'CABINET'];
const STATUSES: LegalOpinionStatus[] = ['REQUESTED', 'IN_REVIEW', 'OPINION_ISSUED', 'WITHDRAWN'];

const TABS = [
  { key: 'request', label: 'Request an opinion' },
  { key: 'queue', label: 'Opinion queue' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * POST /legal-opinions is open to any authenticated T1+ MDA officer; GET
 * (the AG Chambers working queue) requires LEGISLATIVE_DRAFTER — reused
 * fleet-wide in this module as the general "AG Chambers legal officer"
 * role rather than inventing a fourth role code (see
 * m38-attorney-general/src/routes/legal-opinions.ts's header comment).
 */
export function LegalOpinions() {
  const { persona } = useAuth();
  const isCounsel = persona?.roles.includes('LEGISLATIVE_DRAFTER') ?? false;
  const [tab, setTab] = useState<TabKey>(isCounsel ? 'queue' : 'request');

  return (
    <div>
      <PageHeader title="Legal Opinions" subtitle="MDA requests for legal advice from the Attorney General's Chambers." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'request' ? <RequestPanel /> : null}
      {tab === 'queue' ? <QueuePanel isCounsel={isCounsel} /> : null}
    </div>
  );
}

function RequestPanel() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const [requestingMda, setRequestingMda] = useState(persona?.org ?? '');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [urgency, setUrgency] = useState<LegalOpinionUrgency>('ROUTINE');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<LegalOpinionRequest>('/api/v1/m38/legal-opinions', { requestingMda, subject, question, urgency });
      navigate(`/ag/legal-opinions/${created.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="card space-y-3 p-5">
        <Field label="Requesting MDA">
          <input className="field-input" value={requestingMda} onChange={(e) => setRequestingMda(e.target.value)} />
        </Field>
        <Field label="Subject">
          <input className="field-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Interpretation of s.12 Public Procurement Act" />
        </Field>
        <Field label="Question">
          <textarea className="field-input" rows={5} value={question} onChange={(e) => setQuestion(e.target.value)} />
        </Field>
        <Field label="Urgency">
          <select className="field-input" value={urgency} onChange={(e) => setUrgency(e.target.value as LegalOpinionUrgency)}>
            {URGENCIES.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </Field>
        <button
          className="btn-primary w-full"
          onClick={submit}
          disabled={busy || !requestingMda.trim() || subject.trim().length < 3 || !question.trim()}
        >
          {busy ? 'Requesting…' : 'Request opinion'}
        </button>
      </div>
    </div>
  );
}

function QueuePanel({ isCounsel }: { isCounsel: boolean }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LegalOpinionStatus | ''>('');

  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  const listQ = useApiQuery<Page<LegalOpinionRequest>>(isCounsel ? `/api/v1/m38/legal-opinions?${params.toString()}` : null);

  if (!isCounsel) return <ErrorNote message="Requires LEGISLATIVE_DRAFTER — the opinion queue is not visible to this persona." />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Subject / MDA contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as LegalOpinionStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No opinion requests match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Subject</th>
                <th className="table-head">Requesting MDA</th>
                <th className="table-head">Urgency</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ag/legal-opinions/${o.id}`)}>
                  <td className="table-cell font-medium">{o.subject}</td>
                  <td className="table-cell text-xs">{o.requestingMda}</td>
                  <td className="table-cell text-xs">{o.urgency}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
